import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MetricCell {
  readonly value: number;
  readonly target: number | null;
  readonly isBreach: boolean;
  readonly metricDate: string;
}
interface BcmDashboard {
  readonly metrics: Record<string, MetricCell>;
  readonly drReadinessMinScore: number;
  readonly availabilityTargetPct: number;
}

const METRIC_LABEL_KEYS: Record<string, string> = {
  RECOVERY_SUCCESS_RATE: 'm31.bcm.metricRecoverySuccessRate',
  BACKUP_SUCCESS_RATE: 'm31.bcm.metricBackupSuccessRate',
  AVAILABILITY_PCT: 'm31.bcm.metricAvailabilityPct',
  RTO_ACHIEVEMENT: 'm31.bcm.metricRtoAchievement',
  RPO_ACHIEVEMENT: 'm31.bcm.metricRpoAchievement',
  DR_READINESS_SCORE: 'm31.bcm.metricDrReadinessScore',
  RECOVERY_TREND: 'm31.bcm.metricRecoveryTrend',
  TEST_RESULT: 'm31.bcm.metricTestResult',
};

/**
 * M31 — Business Continuity Management Dashboard (§31 / §23). Executive
 * single-glance resilience posture: the latest global KPI per continuity metric
 * (recovery/backup success, availability, RTO/RPO achievement, DR readiness),
 * each flagged green/red against its target, alongside the tenant DR-readiness
 * floor and availability target (LAW 6 — never hard-coded).
 */
export function BcmDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState<BcmDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: BcmDashboard }>('/v1/bcm/analytics/dashboard', { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const codes = data ? Object.keys(data.metrics) : [];

  return (
    <main aria-label="bcm-dashboard">
      <h1>{t('m31.bcm.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? (
        <p>{t('m31.bcm.loading')}</p>
      ) : (
        <>
          <section aria-label="thresholds" className="thresholds">
            <p aria-label="dr-readiness-floor">{t('m31.bcm.drFloor', { score: data.drReadinessMinScore })}</p>
            <p aria-label="availability-target">{t('m31.bcm.availabilityTarget', { pct: data.availabilityTargetPct })}</p>
          </section>
          <p aria-label="metric-count">{t('m31.bcm.count', { count: codes.length })}</p>
          {codes.length === 0 ? (
            <p aria-label="empty-metrics">{t('m31.bcm.empty')}</p>
          ) : (
            <section aria-label="kpi-cards" className="card-grid">
              {codes.map((code) => {
                const m = data.metrics[code];
                return (
                  <article key={code} aria-label={`card-${code}`} data-breach={m.isBreach ? 'yes' : 'no'}>
                    <h2>{METRIC_LABEL_KEYS[code] ? t(METRIC_LABEL_KEYS[code]) : code}</h2>
                    <p aria-label={`value-${code}`}>{m.value}</p>
                    <p aria-label={`target-${code}`}>{m.target === null ? t('m31.bcm.noTarget') : t('m31.bcm.target', { target: m.target })}</p>
                    <p aria-label={`status-${code}`}>{m.isBreach ? 'BREACH' : 'OK'}</p>
                  </article>
                );
              })}
            </section>
          )}
        </>
      )}
    </main>
  );
}
